// ═════════════════════════════════════════════════════════════════════════════
// IPD Package Billing — Package Admin (Stage A) API client
//
// Wraps the EMR package-master endpoints under /app/api/ipd/billing/packages/,
// package-versions/, package-tariffs/, package-components/, charge-heads/ (see
// readmd/ipd-package-billing-flow 1.md). Same conventions as corporate-api.js:
// the UI never talks to the billing microservice directly, only EMR wrappers.
//
// Real, already-deployed endpoints reused as-is (NOT duplicated here):
//   listServiceGroups / listServiceClassifications / listMedicalServices  → billing-api.js
//   listCorporatePayers                                                  → corporate-api.js
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import { getBillingHeaders, trimId, sanitizeErrorMessage } from "./billing-api";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const PACKAGES_ROOT = `${API_ROOT}ipd/billing/packages`;
const VERSIONS_ROOT = `${API_ROOT}ipd/billing/package-versions`;
const TARIFFS_ROOT = `${API_ROOT}ipd/billing/package-tariffs`;
const COMPONENTS_ROOT = `${API_ROOT}ipd/billing/package-components`;
const CHARGE_HEADS_ROOT = `${API_ROOT}ipd/billing/charge-heads`;
const BILLING_ACCOUNTS_ROOT = `${API_ROOT}ipd/billing/billing-accounts`;

// ── low-level helpers ─────────────────────────────────────────────────────────

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

function unwrapObject(data) {
  if (!data || typeof data !== "object") return data;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

function unwrapList(data, keys = []) {
  if (Array.isArray(data)) return data;
  for (const key of ["items", "data", "results", ...keys]) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object" && Array.isArray(val.items)) return val.items;
  }
  return [];
}

/** JSON request with the shared billing error handling (same shape as corporate-api.js). */
async function request(method, url, body) {
  try {
    const res = await axios({
      method,
      url,
      data: body,
      headers: getBillingHeaders(body != null),
    });
    if (res.data?.status === "Failed") {
      const wrapped = new Error(sanitizeErrorMessage(res.data.message || "Request failed"));
      wrapped.status = res.data.status_code || res.data.code;
      throw wrapped;
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    if (err && err.status !== undefined && !err.response) throw err;
    const status = err.response?.status;
    const rawMsg =
      err.response?.data?.message ||
      err.response?.data?.detail ||
      (typeof err.response?.data === "string" ? err.response.data : "") ||
      err.message ||
      "Request failed";
    const wrapped = new Error(sanitizeErrorMessage(rawMsg, status));
    wrapped.status = status;
    wrapped.code = err.response?.data?.code || err.response?.data?.error_code;
    throw wrapped;
  }
}

/** "" / undefined → null, otherwise Number(v) — for optional numeric fields the form sends as "". */
function optionalNumber(v) {
  return v != null && v !== "" ? num(v) : null;
}

/** Newest first — the UI treats versions[0] as "latest" (draft-create gating, default tab selection). */
function sortVersionsDesc(versions) {
  return [...versions].sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
}

// ── mappers (API → UI view model) ────────────────────────────────────────────

export function mapPackage(p = {}) {
  return {
    id: p.id,
    code: p.package_code,
    name: p.package_name,
    category: p.package_category,
    encounterType: p.encounter_type,
    bedTypeCode: p.bed_type_code ?? null,
    isMultiVisit: p.is_multi_visit,
    status: p.package_status,
    active: p.is_active,
    effectiveFrom: p.effective_from,
    createdAt: p.created_at,
  };
}

// Duration/effective_from live on the VERSION (not the package header) in the
// real API — even though POST /packages/ accepts them at create time, the
// backend stores + echoes them back on the auto-created v1 version.
export function mapVersion(v = {}) {
  return {
    id: v.id,
    packageId: v.package_id,
    versionNumber: v.version_number,
    status: v.version_status,
    effectiveFrom: v.effective_from,
    effectiveTo: v.effective_to ?? null,
    durationValue: v.duration_value,
    durationUnit: v.duration_unit,
    publishedAt: v.published_at,
    createdAt: v.created_at,
  };
}

export function mapTariff(t = {}) {
  return {
    id: t.id,
    versionId: t.package_version_id,
    tariffType: t.tariff_type,
    payerId: t.payer_id,
    payerName: t.payer_name,
    bedType: t.bed_type_code ?? null,
    name: t.tariff_name,
    packagePrice: num(t.package_price),
    standardPackagePrice: t.standard_package_price != null ? num(t.standard_package_price) : null,
    payerDiscountAmount: t.payer_discount_amount != null ? num(t.payer_discount_amount) : null,
    effectiveFrom: t.effective_from,
    effectiveTo: t.effective_to,
    priority: t.priority,
    active: t.is_active,
  };
}

export function mapComponent(c = {}) {
  return {
    id: c.id,
    versionId: c.package_version_id,
    parentComponentId: c.parent_component_id,
    level: c.component_level,
    inclusionType: c.inclusion_type,
    bedType: c.bed_type_code ?? null,
    chargeHeadId: c.charge_head_id,
    serviceGroupId: c.service_group_id,
    serviceClassificationId: c.service_classification_id,
    medicalServiceId: c.medical_service_id,
    medicalProductId: c.medical_product_id,
    roomCategoryId: c.room_category_id,
    inventoryScopeCode: c.inventory_scope_code ?? null,
    referenceName: c.reference_name_snapshot,
    limitType: c.limit_type,
    itemLimit: c.item_limit != null ? num(c.item_limit) : null,
    amountLimit: c.amount_limit != null ? num(c.amount_limit) : null,
    limitScope: c.limit_scope,
    excessHandling: c.excess_handling,
    sequenceNumber: c.sequence_number,
  };
}

export function mapChargeHead(h = {}) {
  return {
    id: h.id,
    code: h.head_code,
    name: h.head_name,
    type: h.head_type,
    active: h.is_active,
  };
}

// ── Package header ───────────────────────────────────────────────────────────

export async function listPackages({ search, status, activeOnly = true } = {}) {
  const data = await request(
    "GET",
    `${PACKAGES_ROOT}/${qs({ search, status, active_only: activeOnly })}`
  );
  return unwrapList(data, ["packages"]).map(mapPackage);
}

export async function getPackage(packageId) {
  const data = await request("GET", `${PACKAGES_ROOT}/${trimId(packageId)}/`);
  const obj = unwrapObject(data);
  return {
    package: mapPackage(obj.package || obj),
    versions: sortVersionsDesc((obj.versions || []).map(mapVersion)),
  };
}

export async function createPackage(body) {
  const data = await request("POST", `${PACKAGES_ROOT}/`, {
    package_code: body.package_code?.trim(),
    package_name: body.package_name?.trim() || body.package_code?.trim(),
    package_category: body.package_category || "GENERAL",
    encounter_type: body.encounter_type || "IPD",
    is_multi_visit: Boolean(body.is_multi_visit),
    bed_type_code: body.bed_type_code || undefined,
    effective_from: body.effective_from,
    duration_value: body.duration_value != null ? Number(body.duration_value) : undefined,
    duration_unit: body.duration_unit || "DAY",
  });
  const obj = unwrapObject(data);
  return { package: mapPackage(obj.package || obj) };
}

export async function updatePackageHeader(packageId, body) {
  const data = await request("POST", `${PACKAGES_ROOT}/${trimId(packageId)}/update/`, {
    package_name: body.package_name?.trim(),
    package_category: body.package_category,
    encounter_type: body.encounter_type,
    is_multi_visit: Boolean(body.is_multi_visit),
    bed_type_code: body.bed_type_code || undefined,
  });
  const obj = unwrapObject(data);
  return mapPackage(obj.package || obj);
}

export async function deactivatePackage(packageId) {
  const data = await request("POST", `${PACKAGES_ROOT}/${trimId(packageId)}/deactivate/`);
  const obj = unwrapObject(data);
  return mapPackage(obj.package || obj);
}

export async function copyPackage(packageId, body) {
  const data = await request("POST", `${PACKAGES_ROOT}/${trimId(packageId)}/copy/`, {
    package_code: body.package_code?.trim(),
    package_name: body.package_name?.trim(),
    source_version_id: body.source_version_id || undefined,
    effective_from: body.effective_from || undefined,
  });
  const obj = unwrapObject(data);
  return { package: mapPackage(obj.package || obj) };
}

// ── Versions ──────────────────────────────────────────────────────────────

export async function listPackageVersions(packageId) {
  const data = await request("GET", `${PACKAGES_ROOT}/${trimId(packageId)}/versions/`);
  return sortVersionsDesc(unwrapList(data, ["versions"]).map(mapVersion));
}

export async function createPackageVersion(packageId) {
  const data = await request("POST", `${PACKAGES_ROOT}/${trimId(packageId)}/versions/`);
  const obj = unwrapObject(data);
  return mapVersion(obj.version || obj);
}

export async function getPackageVersion(versionId) {
  const data = await request("GET", `${VERSIONS_ROOT}/${trimId(versionId)}/`);
  const obj = unwrapObject(data);
  return {
    version: mapVersion(obj.version || obj),
    tariffs: (obj.tariffs || []).map(mapTariff),
    components: (obj.components || []).map(mapComponent),
  };
}

export async function updatePackageVersion(versionId, body) {
  // Send only the fields the caller actually changed. Coercing an absent
  // effective_to to null here would wipe an existing "Effective To" whenever the
  // caller edits just the publish date or effective-from (it passes a partial body).
  const payload = {};
  if (body.effective_from !== undefined) payload.effective_from = body.effective_from;
  if (body.effective_to !== undefined) payload.effective_to = body.effective_to;
  if (body.published_at !== undefined) payload.published_at = body.published_at;
  const data = await request("POST", `${VERSIONS_ROOT}/${trimId(versionId)}/update/`, payload);
  const obj = unwrapObject(data);
  return mapVersion(obj.version || obj);
}

export async function publishPackageVersion(versionId) {
  const data = await request("POST", `${VERSIONS_ROOT}/${trimId(versionId)}/publish/`);
  const obj = unwrapObject(data);
  return {
    version: mapVersion(obj.version || {}),
    package: mapPackage(obj.package || {}),
  };
}

// ── Tariffs ───────────────────────────────────────────────────────────────

export async function listPackageTariffs(versionId) {
  const data = await request("GET", `${VERSIONS_ROOT}/${trimId(versionId)}/tariffs/`);
  return unwrapList(data, ["tariffs"]).map(mapTariff);
}

export async function createPackageTariff(versionId, body) {
  const data = await request("POST", `${VERSIONS_ROOT}/${trimId(versionId)}/tariffs/`, {
    tariff_type: body.tariff_type,
    payer_id: body.payer_id || undefined,
    bed_type_code: body.bed_type_code || undefined,
    tariff_name: body.tariff_name?.trim() || undefined,
    package_price: body.package_price,
    standard_package_price: body.standard_package_price ?? body.package_price,
    payer_discount_amount: body.payer_discount_amount || undefined,
    effective_from: body.effective_from,
    effective_to: body.effective_to || undefined,
    priority: body.priority != null ? Number(body.priority) : undefined,
    is_active: body.is_active ?? true,
  });
  const obj = unwrapObject(data);
  return mapTariff(obj.tariff || obj);
}

export async function updatePackageTariff(tariffId, body) {
  const data = await request("POST", `${TARIFFS_ROOT}/${trimId(tariffId)}/update/`, body);
  const obj = unwrapObject(data);
  return mapTariff(obj.tariff || obj);
}

// Soft delete — POST .../package-tariffs/{id}/delete/ (platform is GET/POST only).
export async function deletePackageTariff(tariffId) {
  await request("POST", `${TARIFFS_ROOT}/${trimId(tariffId)}/delete/`, {});
  return { success: true };
}

// ── Components ───────────────────────────────────────────────────────────

export async function listPackageComponents(versionId) {
  const data = await request("GET", `${VERSIONS_ROOT}/${trimId(versionId)}/components/`);
  return unwrapList(data, ["components"]).map(mapComponent);
}

export async function createPackageComponent(versionId, body) {
  const data = await request("POST", `${VERSIONS_ROOT}/${trimId(versionId)}/components/`, {
    component_level: body.component_level,
    parent_component_id: body.parent_component_id || undefined,
    inclusion_type: body.inclusion_type || "INCLUDE",
    bed_type_code: body.bed_type_code || undefined,
    charge_head_id: body.charge_head_id || undefined,
    service_group_id: body.service_group_id || undefined,
    service_classification_id: body.service_classification_id || undefined,
    medical_service_id: body.medical_service_id || undefined,
    medical_product_id: body.medical_product_id || undefined,
    room_category_id: body.room_category_id || undefined,
    inventory_scope_code: body.inventory_scope_code || undefined,
    reference_name_snapshot: body.reference_name_snapshot || undefined,
    limit_type: body.limit_type || "NO_LIMIT",
    item_limit: optionalNumber(body.item_limit),
    amount_limit: optionalNumber(body.amount_limit),
    limit_scope: body.limit_scope || "PACKAGE",
    excess_handling: body.excess_handling || "OUT_OF_PACKAGE",
    sequence_number: body.sequence_number != null ? Number(body.sequence_number) : undefined,
  });
  const obj = unwrapObject(data);
  return mapComponent(obj.component || obj);
}

export async function updatePackageComponent(componentId, body) {
  const data = await request("POST", `${COMPONENTS_ROOT}/${trimId(componentId)}/update/`, {
    ...body,
    item_limit: body.item_limit !== undefined ? optionalNumber(body.item_limit) : undefined,
    amount_limit: body.amount_limit !== undefined ? optionalNumber(body.amount_limit) : undefined,
  });
  const obj = unwrapObject(data);
  return mapComponent(obj.component || obj);
}

// Soft delete — POST .../package-components/{id}/delete/ (platform is GET/POST only).
export async function deletePackageComponent(componentId) {
  await request("POST", `${COMPONENTS_ROOT}/${trimId(componentId)}/delete/`, {});
  return { success: true };
}

// ── Charge heads ──────────────────────────────────────────────────────────

export async function listChargeHeads({ activeOnly = true } = {}) {
  const data = await request("GET", `${CHARGE_HEADS_ROOT}/${qs({ active_only: activeOnly })}`);
  return unwrapList(data, ["charge_heads"]).map(mapChargeHead);
}

export async function createChargeHead(body) {
  const data = await request("POST", `${CHARGE_HEADS_ROOT}/`, {
    head_code: body.head_code?.trim(),
    head_name: body.head_name?.trim() || body.head_code?.trim(),
    head_type: body.head_type || "SERVICE",
    is_active: body.is_active ?? true,
  });
  const obj = unwrapObject(data);
  return mapChargeHead(obj.charge_head || obj);
}

export async function updateChargeHead(chargeHeadId, body) {
  const data = await request("POST", `${CHARGE_HEADS_ROOT}/${trimId(chargeHeadId)}/update/`, body);
  const obj = unwrapObject(data);
  return mapChargeHead(obj.charge_head || obj);
}

// ── Admission-scoped: billing setup, package status, assignment (Modules 3-4) ─

/**
 * POST /ipd_admission/{admission_id}/billing_setup/ — confirmed idempotent live:
 * calling it again on an already-set-up admission returns the same billing IDs
 * with status Success (message "Billing already linked for this admission"),
 * not an error. Safe to call whenever a screen needs billing_account_id.
 */
export async function ensureBillingSetup(admissionId) {
  const data = await request("POST", `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing_setup/`, {});
  const obj = unwrapObject(data);
  return {
    billingAccountId: obj.billing_account_id,
    billingEncounterId: obj.billing_encounter_id,
    billingPatientId: obj.billing_patient_id,
    billingPayerId: obj.billing_payer_id,
    payerType: obj.payer_type,
    billingAccountNumber: obj.billing_account_number,
  };
}

function mapPackageQuickHeader(h = {}) {
  return {
    assignmentId: h.assignment_id ?? h.id ?? null,
    packageCode: h.package_code ?? null,
    packageName: h.package_name ?? null,
    appliedPackagePrice: h.applied_package_price != null ? num(h.applied_package_price) : null,
    status: h.assignment_status ?? h.status ?? null,
    assignedAt: h.assigned_at ?? h.created_at ?? null,
  };
}

/**
 * GET /ipd/billing/billing-accounts/{billing_account_id}/package/ — returns
 * `null` (not a thrown error) when no package is assigned. Confirmed live: an
 * unassigned account responds HTTP 400 `PACKAGE_ASSIGNMENT_NOT_FOUND: <id>`,
 * which is the normal "nothing assigned yet" case for this UI, not a failure.
 */
export async function getPackageQuickHeader(billingAccountId) {
  try {
    const data = await request("GET", `${BILLING_ACCOUNTS_ROOT}/${trimId(billingAccountId)}/package/`);
    return mapPackageQuickHeader(unwrapObject(data));
  } catch (err) {
    if (String(err.message || "").includes("PACKAGE_ASSIGNMENT_NOT_FOUND")) return null;
    throw err;
  }
}

/**
 * GET /ipd/billing/billing-accounts/{billing_account_id}/packages/ — returns ALL
 * active package assignments for the account (multi-package), in priority order
 * (first assigned first). Empty array when none are assigned.
 */
export async function listBillingAccountPackages(billingAccountId) {
  const data = await request("GET", `${BILLING_ACCOUNTS_ROOT}/${trimId(billingAccountId)}/packages/`);
  return unwrapList(unwrapObject(data), ["packages", "assignments", "items"]).map(mapAssignment);
}

/**
 * POST /ipd_admission/{admission_id}/billing/package-assign/
 * body: { package_id, package_version_id?, payer_id?, create_initial_visit, remarks }
 */
export async function assignPackage(admissionId, body) {
  const data = await request(
    "POST",
    `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing/package-assign/`,
    {
      package_id: body.package_id,
      package_version_id: body.package_version_id || undefined,
      payer_id: body.payer_id || undefined,
      create_initial_visit: body.create_initial_visit ?? true,
      remarks: body.remarks || undefined,
    }
  );
  const obj = unwrapObject(data);
  return mapAssignment(obj.data || obj);
}

function mapAssignment(a = {}) {
  return {
    id: a.id,
    assignmentNumber: a.assignment_number,
    packageCode: a.package_code,
    packageName: a.package_name ?? null,
    status: a.assignment_status,
    appliedPackagePrice: a.applied_package_price != null ? num(a.applied_package_price) : null,
    standardPackagePrice: a.standard_package_price != null ? num(a.standard_package_price) : null,
    billingAccountId: a.billing_account_id,
    primaryEncounterId: a.primary_encounter_id,
    assignedAt: a.assigned_at ?? a.created_at ?? null,
    cancelledAt: a.cancelled_at ?? null,
    cancellationReason: a.cancellation_reason ?? null,
    completedAt: a.completed_at ?? null,
    remarks: a.remarks ?? null,
  };
}

/** Friendly text for the documented package-assign error cases; falls back to sanitizeErrorMessage's own cleanup. */
export function describeAssignError(err) {
  const raw = String(err?.message || "");
  if (raw.includes("PACKAGE_ALREADY_ASSIGNED") || raw.includes("already assigned"))
    return "This package is already assigned to the admission. Pick a different package to add.";
  if (raw.includes("must be published"))
    return "Selected package version isn't published yet.";
  if (raw.includes("No active package tariff"))
    return "This package has no active tariff — contact the billing admin.";
  if (raw.toLowerCase().includes("billing account") && (raw.toLowerCase().includes("not open") || raw.toLowerCase().includes("not found")))
    return "Billing account isn't open for this admission.";
  return raw && raw.length < 140 ? raw : "Could not assign package";
}

// ── Package dashboard (Module 5): assignment detail, entitlements, utilisation,
// charge-sheet, and lifecycle actions ────────────────────────────────────────

/** GET /ipd/billing/package-assignments/{assignment_id}/ */
export async function getPackageAssignment(assignmentId) {
  const data = await request("GET", `${API_ROOT}ipd/billing/package-assignments/${trimId(assignmentId)}/`);
  const obj = unwrapObject(data);
  return mapAssignment(obj.assignment || obj.data || obj);
}

function mapEntitlement(e = {}) {
  return {
    id: e.id ?? null,
    serviceCode: e.service_code ?? null,
    referenceName: e.reference_name_snapshot ?? e.service_name ?? e.service_code ?? "—",
    inclusionType: e.inclusion_type ?? null,
    limitType: e.limit_type ?? null,
    originalItemLimit: e.original_item_limit != null ? num(e.original_item_limit) : null,
    amountLimit: e.amount_limit != null ? num(e.amount_limit) : null,
    consumedQuantity: e.consumed_quantity != null ? num(e.consumed_quantity) : null,
    consumedAmount: e.consumed_amount != null ? num(e.consumed_amount) : null,
    remainingQuantity: e.remaining_quantity != null ? num(e.remaining_quantity) : null,
    remainingAmount: e.remaining_amount != null ? num(e.remaining_amount) : null,
  };
}

/** GET /ipd/billing/package-assignments/{assignment_id}/entitlements/ — frozen rules. */
export async function getEntitlements(assignmentId) {
  const data = await request("GET", `${API_ROOT}ipd/billing/package-assignments/${trimId(assignmentId)}/entitlements/`);
  const obj = unwrapObject(data);
  return unwrapList(obj, ["entitlements"]).map(mapEntitlement);
}

/** GET /ipd/billing/package-assignments/{assignment_id}/utilisation/ — same row shape as entitlements, with consumed/remaining filled in. */
export async function getUtilisation(assignmentId) {
  const data = await request("GET", `${API_ROOT}ipd/billing/package-assignments/${trimId(assignmentId)}/utilisation/`);
  const obj = unwrapObject(data);
  return unwrapList(obj, ["entitlements"]).map(mapEntitlement);
}

function mapChargeSheetItem(it = {}) {
  return {
    chargeEventId: it.charge_event_id,
    serviceCode: it.service_code,
    serviceName: it.service_name,
    quantity: it.quantity != null ? num(it.quantity) : null,
    grossAmount: it.gross_amount != null ? num(it.gross_amount) : null,
    isPackageCharge: it.is_package_charge === true,
    packagePricingStatus: it.package_pricing_status ?? null,
  };
}

/**
 * GET /ipd/billing/billing-accounts/{billing_account_id}/charge-sheet/ — live
 * shape confirmed against manage-dev: {data: {billing_account_id,
 * package_assignment_id, package_code, package_name, items: [...], total}}.
 * `total` is an item COUNT, not a money total — in/out-of-package totals are
 * computed client-side from `items` unless the backend adds a `summary` block.
 */
export async function getChargeSheet(billingAccountId) {
  const data = await request("GET", `${BILLING_ACCOUNTS_ROOT}/${trimId(billingAccountId)}/charge-sheet/`);
  const obj = unwrapObject(data);
  const items = (obj.items || []).map(mapChargeSheetItem);
  return {
    billingAccountId: obj.billing_account_id,
    packageAssignmentId: obj.package_assignment_id ?? null,
    packageCode: obj.package_code ?? null,
    packageName: obj.package_name ?? null,
    items,
    inPackageTotal: obj.summary?.in_package_total != null
      ? num(obj.summary.in_package_total)
      : items.filter((i) => i.isPackageCharge).reduce((s, i) => s + (i.grossAmount || 0), 0),
    outOfPackageTotal: obj.summary?.out_of_package_total != null
      ? num(obj.summary.out_of_package_total)
      : items.filter((i) => !i.isPackageCharge).reduce((s, i) => s + (i.grossAmount || 0), 0),
  };
}

function currentUsername() {
  return sessionStorage.getItem("userName") || sessionStorage.getItem("username") || "";
}

/** POST /ipd/billing/package-assignments/{assignment_id}/cancel/ */
export async function cancelPackageAssignment(assignmentId, cancellationReason) {
  const data = await request(
    "POST",
    `${API_ROOT}ipd/billing/package-assignments/${trimId(assignmentId)}/cancel/`,
    { cancelled_by: currentUsername(), cancellation_reason: cancellationReason }
  );
  const obj = unwrapObject(data);
  return mapAssignment(obj.data || obj);
}

/** POST /ipd/billing/package-assignments/{assignment_id}/complete/ */
export async function completePackageAssignment(assignmentId, remarks) {
  const data = await request(
    "POST",
    `${API_ROOT}ipd/billing/package-assignments/${trimId(assignmentId)}/complete/`,
    { completed_by: currentUsername(), remarks: remarks || undefined }
  );
  const obj = unwrapObject(data);
  return mapAssignment(obj.data || obj);
}
